// Sfondo seta animato (WebGL). Nessun video, nessun loop che riparte: il movimento e' continuo.
// Tutte le onde usano multipli interi della stessa fase, quindi il ciclo si richiude da solo.
export function createSilkBackground(canvas, imageUrl, options) {
  var o = Object.assign({
    brightness: 0.78,    // luminosita' generale (piu' basso = piu' scuro)
    saturation: 0.62,    // 1 = colori originali, piu' basso = oro piu' discreto
    sweep: 0.16,         // intensita' del riflesso dorato che scorre piano
    warp: 0.009,         // ampiezza delle onde del tessuto (piccola ma visibile)
    period: 14,          // secondi per un ciclo completo (piu' basso = piu' veloce)
    fps: 30,
    maxPixels: 2600000   // limite pixel per tenere fluido il telefono
  }, options || {});
  var gl = canvas.getContext("webgl", { antialias: false, alpha: false, powerPreference: "low-power" });
  if (!gl) { return { ok: false, destroy: function () {} }; }
  var TAU = Math.PI * 2;
  var reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var VERT = "attribute vec2 a; void main(){ gl_Position = vec4(a, 0.0, 1.0); }";
  var FRAG = [
    "#ifdef GL_FRAGMENT_PRECISION_HIGH",
    "precision highp float;",
    "#else",
    "precision mediump float;",
    "#endif",
    "uniform sampler2D tex; uniform vec2 r; uniform vec2 ts;",
    "uniform float p; uniform float scroll;",
    "uniform float bright; uniform float sat; uniform float sweep; uniform float warp;",
    "float hash(vec2 q){ return fract(sin(dot(q, vec2(12.9898, 78.233))) * 43758.5453); }",
    "void main(){",
    "  float cover = max(r.x / ts.x, r.y / ts.y) * 1.07;",
    "  vec2 size = ts * cover;",
    "  vec2 uv = (gl_FragCoord.xy - 0.5 * r) / size + 0.5;",
    "  uv.y += scroll;",
    "  vec2 w = vec2(",
    "    sin(uv.y * 7.0 + p) + 0.5 * sin(uv.y * 13.0 - 2.0 * p),",
    "    cos(uv.x * 6.0 + p) + 0.5 * cos(uv.x * 11.0 + 2.0 * p));",
    "  uv += w * warp;",
    "  vec3 c = texture2D(tex, clamp(uv, 0.001, 0.999)).rgb;",
    "  float l0 = dot(c, vec3(0.299, 0.587, 0.114));",
    "  float band = sin((uv.x * 0.9 + uv.y * 1.4) * 6.2831853 - p);",
    "  float sw = smoothstep(0.35, 1.0, band);",
    "  c += vec3(1.0, 0.78, 0.45) * sw * pow(l0, 1.5) * sweep;",
    "  float l = dot(c, vec3(0.299, 0.587, 0.114));",
    "  c = vec3(l) + (c - vec3(l)) * sat;",
    "  c = c / (1.0 + 0.55 * c) * 1.45;",
    "  c = pow(clamp(c, 0.0, 1.0), vec3(1.12)) * bright;",
    "  c += (hash(gl_FragCoord.xy) - 0.5) * 0.008;",
    "  gl_FragColor = vec4(c, 1.0);",
    "}"
  ].join("\n");
  function shader(type, src) {
    var s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    return gl.getShaderParameter(s, gl.COMPILE_STATUS) ? s : null;
  }
  var vs = shader(gl.VERTEX_SHADER, VERT), fs = shader(gl.FRAGMENT_SHADER, FRAG);
  if (!vs || !fs) { return { ok: false, destroy: function () {} }; }
  var prog = gl.createProgram();
  gl.attachShader(prog, vs); gl.attachShader(prog, fs);
  gl.bindAttribLocation(prog, 0, "a");
  gl.linkProgram(prog);
  if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) { return { ok: false, destroy: function () {} }; }
  gl.useProgram(prog);
  var buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  var U = {};
  ["tex", "r", "ts", "p", "scroll", "bright", "sat", "sweep", "warp"].forEach(function (n) {
    U[n] = gl.getUniformLocation(prog, n);
  });
  var texSize = [1, 1], ready = false, raf = 0, last = 0, stopped = false;
  var start = performance.now();
  var img = new Image();
  img.crossOrigin = "anonymous";
  img.onload = function () {
    var tx = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, tx);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    texSize = [img.naturalWidth, img.naturalHeight];
    ready = true;
    draw(performance.now());
    if (!reduce) { raf = requestAnimationFrame(loop); }
  };
  img.src = imageUrl;
  function resize() {
    var cw = canvas.clientWidth || window.innerWidth, ch = canvas.clientHeight || window.innerHeight;
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    dpr = Math.min(dpr, Math.sqrt(o.maxPixels / (cw * ch)));
    var w = Math.max(1, Math.round(cw * dpr)), h = Math.max(1, Math.round(ch * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
    gl.viewport(0, 0, canvas.width, canvas.height);
  }
  function draw(now) {
    if (!ready) { return; }
    resize();
    var sec = ((now - start) / 1000) % o.period;          // si riavvolge dove il ciclo e' identico
    var p = reduce ? 1.2 : (sec / o.period) * TAU;
    var sc = -Math.min(window.scrollY || 0, 1200) / Math.max(canvas.clientHeight, 1) * 0.02;
    gl.uniform1i(U.tex, 0);
    gl.uniform2f(U.r, canvas.width, canvas.height);
    gl.uniform2f(U.ts, texSize[0], texSize[1]);
    gl.uniform1f(U.p, p);
    gl.uniform1f(U.scroll, reduce ? 0 : sc);
    gl.uniform1f(U.bright, o.brightness);
    gl.uniform1f(U.sat, o.saturation);
    gl.uniform1f(U.sweep, o.sweep);
    gl.uniform1f(U.warp, o.warp);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }
  function loop(now) {
    if (stopped) { return; }
    if (now - last >= 1000 / o.fps - 2) { last = now; draw(now); }
    raf = requestAnimationFrame(loop);
  }
  function onVisibility() {
    if (reduce) { return; }
    if (document.hidden) { cancelAnimationFrame(raf); }
    else if (!stopped) { raf = requestAnimationFrame(loop); }
  }
  function onResize() { if (reduce) { draw(performance.now()); } }
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("resize", onResize);
  return {
    ok: true,
    destroy: function () {
      stopped = true;
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("resize", onResize);
    }
  };
}
